/*
 * Pure view-model for the My Leads / Team's Leads tabs (PROD-9977): period, refresh timing,
 * IST formatting, label maps for API kinds, per-level tiles and sections, shift chart.
 * `now` is always a parameter so tests pin the clock.
 */
import { fmtTime } from '../session_allocation/allocation';
import { LEVELS } from '../level';
import { OUTCOMES } from '../outcome';
import type { Tone } from '../ui';
import type { RepLevel, TeamMember } from '../../sales_team/api';
import type {
  JoinedLead,
  LeadsPeriod,
  LeadsShift,
  MedianCounts,
  MyLeadsData,
  NowWith,
  Signal,
  Touch,
  TrialStatus
} from './types';

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
// IST has no DST.
const IST_OFFSET_MS = 330 * MINUTE_MS;

export const PERIODS: LeadsPeriod[] = [7, 14, 21, 35];
export const DEFAULT_PERIOD: LeadsPeriod = 7;
// Server cache TTL; also the poll interval when the server's interval is unknown.
export const POLL_FALLBACK_MS = 5 * MINUTE_MS;
// Poll just after the server cache expires, never in a tight loop.
export const REFRESH_GRACE_MS = 2000;
export const MIN_POLL_MS = 15 * 1000;
// A touch older than this reads as "No touch for N days".
const STALE_TOUCH_DAYS = 7;

export type CellTone = 'plain' | 'muted' | 'success' | 'warning' | 'error' | 'info';
export interface Cell {
  text: string;
  tone: CellTone;
}

const toMs = (v: string | number | null | undefined): number => {
  if (typeof v === 'number') return v;
  if (!v) return NaN;
  return Date.parse(v);
};

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

// One decimal at most: 2.1, 3.
const fmtNum = (v: number): string => String(Math.round(v * 10) / 10);

const humanize = (code: string): string => {
  const s = code.replace(/_/g, ' ').toLowerCase().trim();
  return s ? s[0].toUpperCase() + s.slice(1) : '';
};

// Level chip tone, shared by the page header and the Team's Leads picker.
export const LEVEL_TONE: Record<RepLevel, Tone> = { L1: 'info', L2: 'brand', L3: 'warning' };

export const parsePeriod = (v: unknown): LeadsPeriod => {
  const n = Number(v);
  return (PERIODS as number[]).includes(n) ? (n as LeadsPeriod) : DEFAULT_PERIOD;
};

export const periodLabel = (p: LeadsPeriod): string => `Last ${p} days`;

// ---- refresh timing ----

type Stamp = string | null | undefined;

// How long to wait from arrival, all on the server's clock (skew-free): what is left of the cache entry
// (next_refresh_at - server_time), else the full generated_at interval for older payloads. null = unknown.
const serverWaitMs = (
  generatedAt: Stamp,
  nextRefreshAt: Stamp,
  serverTime: Stamp
): number | null => {
  const next = toMs(nextRefreshAt);
  if (Number.isNaN(next)) return null;
  const served = toMs(serverTime);
  // May be <= 0 for an entry at its expiry; clampPoll floors that at MIN_POLL_MS.
  if (!Number.isNaN(served)) return next - served;
  const iv = next - toMs(generatedAt);
  return Number.isNaN(iv) || iv <= 0 ? null : iv;
};

const clampPoll = (ms: number): number =>
  Math.min(Math.max(ms, MIN_POLL_MS), POLL_FALLBACK_MS + REFRESH_GRACE_MS);

// Client time of the next poll: the server's remaining wait counted from when the response arrived.
const pollDueAt = (
  generatedAt: Stamp,
  nextRefreshAt: Stamp,
  arrivedAt: number,
  serverTime: Stamp
): number => {
  const wait = serverWaitMs(generatedAt, nextRefreshAt, serverTime);
  return arrivedAt + (wait === null ? POLL_FALLBACK_MS : clampPoll(wait + REFRESH_GRACE_MS));
};

export const nextPollDelay = (
  generatedAt: Stamp,
  nextRefreshAt: Stamp,
  arrivedAt: number,
  now: number,
  serverTime?: Stamp
): number => {
  if (serverWaitMs(generatedAt, nextRefreshAt, serverTime) === null) return POLL_FALLBACK_MS;
  return clampPoll(pollDueAt(generatedAt, nextRefreshAt, arrivedAt, serverTime) - now);
};

// Client time the server cache expires, for the countdown: the poll due time less the grace.
export const refreshDueAt = (
  generatedAt: Stamp,
  nextRefreshAt: Stamp,
  arrivedAt: number,
  serverTime?: Stamp
): number | null => {
  if (serverWaitMs(generatedAt, nextRefreshAt, serverTime) === null) return null;
  return pollDueAt(generatedAt, nextRefreshAt, arrivedAt, serverTime) - REFRESH_GRACE_MS;
};

// Tab-return catch-up: only at the same due time as the timer, and never within MIN_POLL_MS
// of the last load.
export const shouldCatchUp = (
  generatedAt: Stamp,
  nextRefreshAt: Stamp,
  arrivedAt: number,
  lastLoadAt: number,
  now: number,
  missedPoll: boolean,
  serverTime?: Stamp
): boolean => {
  if (now - lastLoadAt < MIN_POLL_MS) return false;
  if (missedPoll) return true;
  return now >= pollDueAt(generatedAt, nextRefreshAt, arrivedAt, serverTime);
};

export const countdownLabel = (dueAt: number | null, now: number): string => {
  if (dueAt === null) return '';
  const left = dueAt - now;
  if (left <= 0) return 'Refreshing…';
  const secs = Math.ceil(left / 1000);
  return `Next refresh in ${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
};

// ---- IST formatting ----

export const fmtDay = (at: string | number | null | undefined): string => {
  const t = toMs(at);
  if (Number.isNaN(t)) return '—';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'short'
  }).formatToParts(new Date(t));
  const get = (type: string) => parts.find((p) => p.type === type)?.value || '';
  return `${get('day')} ${get('month')}`;
};

// "29 Oct to 4 Nov" from the payload's IST dates (YYYY-MM-DD); '' when unreadable.
export const periodRange = (
  startIst: string | null | undefined,
  endIst: string | null | undefined
): string => {
  const day = (v: string | null | undefined) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v || '');
    return m ? fmtDay(`${m[1]}-${m[2]}-${m[3]}T12:00:00+05:30`) : '—';
  };
  const from = day(startIst);
  const to = day(endIst);
  return from === '—' || to === '—' ? '' : `${from} to ${to}`;
};

export const fmtDayTime = (at: string | number | null | undefined): string => {
  const day = fmtDay(at);
  return day === '—' ? day : `${day}, ${fmtTime(at)}`;
};

export const updatedLabel = (generatedAt: string | null | undefined): string => {
  const time = fmtTime(generatedAt);
  return time ? `Updated ${time} IST` : '';
};

const istDay = (t: number): number => Math.floor((t + IST_OFFSET_MS) / DAY_MS);

const istDaysSince = (at: string | null | undefined, now: number): number => {
  const t = toMs(at);
  if (Number.isNaN(t)) return 0;
  return Math.max(0, istDay(now) - istDay(t));
};

export const agoLabel = (at: string | null | undefined, now: number): string => {
  const n = istDaysSince(at, now);
  if (n === 0) return 'today';
  if (n === 1) return 'yesterday';
  return `${n} days ago`;
};

// ---- date groups for the row tables ----

export interface DateGroup<T> {
  // IST day number (days since epoch); null for rows with an unreadable date.
  day: number | null;
  rows: T[];
}

// Newest day first; the unreadable-date group (day null) last.
const byDayDesc = (a: number | null, b: number | null): number => {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return b - a;
};

// Groups rows by IST calendar day, newest day first, and rows within a day by the same timestamp,
// newest first (unreadable dates keep list order, last). Sorted: L1 rows arrive by booked-on.
export const groupRowsByIstDate = <T>(
  rows: T[],
  getIso: (row: T) => string | null | undefined
): DateGroup<T>[] => {
  const byDay = new Map<number | null, Array<{ row: T; t: number; i: number }>>();
  rows.forEach((row, i) => {
    const t = toMs(getIso(row));
    const day = Number.isNaN(t) ? null : istDay(t);
    const list = byDay.get(day) || [];
    list.push({ row, t: Number.isNaN(t) ? 0 : t, i });
    byDay.set(day, list);
  });
  return Array.from(byDay.keys())
    .sort(byDayDesc)
    .map((day) => ({
      day,
      rows: (byDay.get(day) || []).sort((x, y) => y.t - x.t || x.i - y.i).map((x) => x.row)
    }));
};

// "Today" / "Yesterday" / "Tomorrow", else "Mon, 3 Nov" (IST).
export const dayHeading = (day: number | null, now: number): string => {
  if (day === null) return 'No date';
  const diff = istDay(now) - day;
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff === -1) return 'Tomorrow';
  // Noon of that IST day, so the formatter cannot slip a day.
  const noon = new Date(day * DAY_MS - IST_OFFSET_MS + 12 * HOUR_MS);
  const weekday = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    weekday: 'short'
  }).format(noon);
  return `${weekday}, ${fmtDay(noon.getTime())}`;
};

export type GroupNoun = 'trial' | 'lead' | 'member';

export const groupCountLabel = (n: number, noun: GroupNoun): string => plural(n, noun, `${noun}s`);

// Phone day heading (no column header there to say which date): "Paid on Mon, 3 Nov",
// "Paid today", "Joined Mon, 3 Nov", "Joined yesterday"; trial tables stay plain.
export type HeadingVerb = 'paid' | 'joined';
export const phoneDayHeading = (heading: string, verb?: HeadingVerb): string => {
  if (!verb) return heading;
  const relative = ['Today', 'Yesterday', 'Tomorrow', 'No date'].includes(heading);
  const lead = verb === 'paid' ? 'Paid' : 'Joined';
  if (relative) return heading === 'No date' ? heading : `${lead} ${heading.toLowerCase()}`;
  return verb === 'paid' ? `Paid on ${heading}` : `Joined ${heading}`;
};

// ---- label maps for API kinds ----

const touchKindLabel = (touch: Touch): string => {
  switch (touch.kind) {
    case 'call':
      return 'Call';
    case 'call_no_answer':
      return 'Call, no answer';
    case 'sms':
      return 'SMS';
    case 'whatsapp':
      return 'WhatsApp';
    case 'note':
      return 'Note';
    case 'task': {
      if (!touch.outcome) return 'Task done';
      const outcome =
        OUTCOMES.find((o) => o.code === touch.outcome)?.label || humanize(touch.outcome);
      return `Task done (${outcome})`;
    }
    default:
      return humanize(String(touch.kind));
  }
};

const SIGNAL_LABELS: Record<string, string> = {
  three_month_plan_opened: '3-month plan opened',
  pricing_page_visited: 'Pricing page visit',
  booked_trial: 'Booked another trial',
  session_joined: 'Joined a session',
  session_finished: 'Finished a session',
  first_pc_booked: 'Booked first 1-on-1',
  trial_reset: 'Trial reset'
};

export const lastTouchCell = (
  touch: Touch | null | undefined,
  joinedAt: string | null | undefined,
  now: number
): Cell => {
  if (!touch) {
    const n = istDaysSince(joinedAt, now);
    return {
      text: n >= 1 ? `No touch for ${plural(n, 'day', 'days')}` : 'No touch yet',
      tone: 'warning'
    };
  }
  const age = istDaysSince(touch.at, now);
  if (age >= STALE_TOUCH_DAYS) return { text: `No touch for ${age} days`, tone: 'warning' };
  return { text: `${touchKindLabel(touch)}, ${agoLabel(touch.at, now)}`, tone: 'plain' };
};

export const touchBeforeLabel = (
  touch: Touch | null | undefined,
  startAt: string | null | undefined
): Cell => {
  if (!touch) return { text: 'No touch before', tone: 'warning' };
  const gap = Math.max(0, toMs(startAt) - toMs(touch.at));
  let when = '';
  if (Number.isNaN(gap)) when = '';
  else if (gap < HOUR_MS) when = `${Math.round(gap / MINUTE_MS)} min before`;
  else if (gap < DAY_MS) when = `${Math.round(gap / HOUR_MS)} h before`;
  else when = `${Math.round(gap / DAY_MS)} d before`;
  return {
    text: when ? `${touchKindLabel(touch)}, ${when}` : touchKindLabel(touch),
    tone: 'plain'
  };
};

export const signalCell = (signal: Signal | null | undefined, now: number): Cell => {
  if (!signal) return { text: '—', tone: 'muted' };
  const label = SIGNAL_LABELS[signal.kind] || humanize(String(signal.kind));
  return { text: `${label}, ${agoLabel(signal.at, now)}`, tone: 'plain' };
};

export const daysLeftCell = (n: number): Cell => {
  if (n <= 0) return { text: 'Ends today', tone: 'error' };
  let tone: CellTone = 'plain';
  if (n <= 3) tone = 'error';
  else if (n <= 7) tone = 'warning';
  return { text: `${n} d`, tone };
};

// Leads I joined status: "Open · 3 d left", "Paid · 30 Oct", "Window ended".
export const leadStatusCell = (row: Pick<JoinedLead, 'status' | 'days_left' | 'paid_at'>): Cell => {
  if (row.status === 'paid') return { text: `Paid · ${fmtDay(row.paid_at)}`, tone: 'success' };
  if (row.status === 'window_ended') return { text: 'Window ended', tone: 'muted' };
  const left = daysLeftCell(row.days_left);
  return {
    text: row.days_left <= 0 ? 'Open · ends today' : `Open · ${row.days_left} d left`,
    tone: left.tone
  };
};

const STATUS_CELLS: Record<TrialStatus, Cell> = {
  attended: { text: 'Attended', tone: 'success' },
  no_show: { text: 'No-show', tone: 'error' },
  cancelled: { text: 'Cancelled', tone: 'muted' },
  upcoming: { text: 'Upcoming', tone: 'info' },
  unknown: { text: 'Under review', tone: 'muted' }
};

// L2's column asks "Joined?", so attended reads as Joined there.
export const trialStatusCell = (status: TrialStatus, level: RepLevel): Cell => {
  if (status === 'attended' && level === 'L2') return { text: 'Joined', tone: 'success' };
  return STATUS_CELLS[status] || { text: humanize(String(status)), tone: 'plain' };
};

export const viaLabel = (via: string): string =>
  via === 'after_task' ? 'After my task' : 'Booked by me';

export const nowWithLabel = (nw: NowWith | null | undefined): string => {
  if (nw?.label) return nw.label;
  if (nw?.kind === 'advisor') return 'Advisor';
  if (nw?.kind === 'task') return 'Open task';
  return 'No one yet';
};

// Mirrors the server mapping; 28 weeks is the 6-month package too.
export const planLabel = (
  weeks: number | null | undefined,
  label: string | null | undefined
): string => {
  if (label) return label;
  if (weeks === 12) return '3 months';
  if (weeks === 24 || weeks === 28) return '6 months';
  if (weeks === 52) return '12 months';
  return 'Monthly';
};

export const daysToPayCell = (n: number | null | undefined): Cell => {
  if (n === null || n === undefined) return { text: '—', tone: 'muted' };
  return { text: fmtNum(n), tone: n <= 2 ? 'success' : 'plain' };
};

// Rates are fractions; a value above 1 is taken as already a percent.
export const pct = (rate: number | null | undefined): string => {
  if (rate === null || rate === undefined || Number.isNaN(rate)) return '—';
  return `${Math.round(rate > 1 ? rate : rate * 100)}%`;
};

// ---- tiles ----

export interface Tile {
  key: string;
  label: string;
  value: string;
  hint: string;
  // The level's success measure is tinted.
  success?: boolean;
}

type MedianKey = keyof MedianCounts;

// "L1 median 19 (6 reps)", using the reps behind that median; blank when none qualified.
const medianText = (
  level: RepLevel,
  d: MyLeadsData,
  key: MedianKey,
  fmt: (v: number) => string
): string => {
  const v = d.medians?.[key];
  const n = d.medians?.counts?.[key] || 0;
  if (n <= 0 || v === null || v === undefined) return '';
  return `${level} median ${fmt(v)} (${plural(n, 'rep', 'reps')})`;
};

const joinHint = (...parts: string[]): string => parts.filter(Boolean).join(' · ');

const ending = (n: number): string => (n > 0 ? `${n} ${n === 1 ? 'ends' : 'end'} this week` : '');

export const tilesFor = (d: MyLeadsData): Tile[] => {
  const { level } = d.rep;
  if (!level) return [];
  const joined = d.leads_joined;
  const leadsTile: Tile = {
    key: 'leads',
    label: 'Leads I joined',
    value: String(joined?.count ?? 0),
    hint: joinHint(`${joined?.open ?? 0} still open`, ending(joined?.ending_this_week ?? 0))
  };
  const convertedTile: Tile = {
    key: 'converted',
    label: 'Joined & converted',
    value: String(d.converted?.count ?? 0),
    hint: 'of leads I joined in the period'
  };

  if (level === 'L1') {
    const { l1 } = d;
    return [
      {
        key: 'booked',
        label: 'Trials booked',
        value: String(l1?.booked ?? 0),
        hint: joinHint('success', medianText('L1', d, 'trials_booked', fmtNum)),
        success: true
      },
      {
        key: 'attended',
        label: 'Booked and attended',
        value: `${l1?.attended ?? 0} of ${l1?.past ?? 0}`,
        hint: 'past trials in the period'
      },
      leadsTile,
      convertedTile
    ];
  }

  if (level === 'L2') {
    const covered = d.l2?.covered ?? 0;
    const past = d.l2?.past ?? 0;
    const trialsJoined = d.l2?.joined ?? 0;
    return [
      {
        key: 'joined',
        label: 'Trials joined',
        value: `${trialsJoined} of ${past}`,
        hint: joinHint(
          'success',
          past > 0 ? pct(trialsJoined / past) : '',
          medianText('L2', d, 'trials_joined_rate', pct)
        ),
        success: true
      },
      {
        key: 'covered',
        label: 'Covered trials',
        value: String(covered),
        hint: joinHint('started in the period', d.l2?.upcoming ? `${d.l2.upcoming} upcoming` : '')
      },
      leadsTile,
      convertedTile
    ];
  }

  const { l3 } = d;
  return [
    {
      ...convertedTile,
      hint: joinHint('success', medianText('L3', d, 'converted', fmtNum)),
      success: true
    },
    leadsTile,
    {
      key: 'conversion',
      label: 'Conversion',
      value: pct(l3?.rate),
      hint: joinHint(
        l3 ? `${l3.converted} of ${l3.cohort}` : '',
        l3 ? `${l3.still_open} still open` : '',
        medianText('L3', d, 'conversion_rate', pct)
      )
    },
    {
      key: 'days_to_pay',
      label: 'Days trial to pay',
      value:
        l3?.median_days_to_pay === null || l3?.median_days_to_pay === undefined
          ? '—'
          : fmtNum(l3.median_days_to_pay),
      hint: medianText('L3', d, 'days_to_pay', fmtNum)
    }
  ];
};

// ---- sections ----

export type SectionId = 'l1_trials' | 'l2_trials' | 'leads_joined' | 'converted';

export const sectionsFor = (level: RepLevel | null | undefined): SectionId[] => {
  if (level === 'L1') return ['l1_trials', 'leads_joined', 'converted'];
  if (level === 'L2') return ['l2_trials', 'leads_joined', 'converted'];
  if (level === 'L3') return ['converted', 'leads_joined'];
  return [];
};

export interface SectionMeta {
  title: string;
  sub: string;
  empty: string;
}

export const sectionMeta = (id: SectionId): SectionMeta => {
  switch (id) {
    case 'l1_trials':
      return {
        title: 'Trials I booked',
        sub: 'booked by me, or by the student within 7 days of my task. Did they turn up?',
        empty: 'No trials booked by you in this period.'
      };
    case 'l2_trials':
      return {
        title: 'My trials joined',
        sub: 'trials that started in the period that I covered with a reminder or join task, and whether the student joined',
        empty: 'No trials covered by you in this period.'
      };
    case 'leads_joined':
      return {
        title: 'Leads I joined',
        sub: 'students who became mine as advisor in the period; their status as of now',
        empty: 'No leads joined in this period.'
      };
    default:
      return {
        title: 'Joined & converted',
        sub: 'leads I joined in the period who have become members so far',
        empty: 'No leads joined in this period have converted yet.'
      };
  }
};

export const sectionCount = (d: MyLeadsData, id: SectionId): string => {
  switch (id) {
    case 'l1_trials': {
      const { l1 } = d;
      if (!l1) return '';
      const review = l1.unknown ? ` · ${l1.unknown} under review` : '';
      return `${l1.booked} booked · ${l1.attended} attended · ${l1.no_show} no-show · ${l1.cancelled} cancelled · ${l1.upcoming} upcoming${review}`;
    }
    case 'l2_trials': {
      const { l2 } = d;
      if (!l2) return '';
      const past = l2.past ?? 0;
      return joinHint(
        `${l2.covered} covered`,
        `${l2.joined} of ${past} joined`,
        past > 0 ? pct(l2.joined / past) : '',
        l2.upcoming ? `${l2.upcoming} upcoming` : ''
      );
    }
    case 'leads_joined': {
      const j = d.leads_joined;
      if (!j) return '';
      return joinHint(
        plural(j.count, 'lead', 'leads'),
        `${j.open ?? 0} open`,
        `${j.paid ?? 0} paid`,
        `${j.window_ended ?? 0} window ended`,
        ending(j.ending_this_week)
      );
    }
    default: {
      const c = d.converted;
      if (!c) return '';
      return joinHint(
        plural(c.count, 'member', 'members'),
        c.three_months_plus > 0 ? `${c.three_months_plus} on 3 months+` : ''
      );
    }
  }
};

// ---- My shift today ----

export interface ShiftStat {
  key: string;
  label: string;
  value: string;
  detail: string;
  // CSS colour token for the stat's left rule.
  color: string;
}

export interface ShiftSegment {
  key: string;
  label: string;
  value: number;
  pct: number;
  color: string;
  opacity?: number;
}

export interface ShiftHourView {
  label: string;
  cameIn: number;
  doneMe: number;
  doneOthers: number;
  pastDue: number | null;
  inPct: number;
  donePct: number;
  doneMePct: number;
  current: boolean;
  future: boolean;
}

// Phone "By hour" row text: "6 in · 2 done" (only when the chart has bars) and "past due 17".
export const hourRowText = (
  h: Pick<ShiftHourView, 'cameIn' | 'doneMe' | 'doneOthers' | 'pastDue'>,
  showBars: boolean
): { counts: string; pastDue: string } => ({
  counts: showBars ? `${h.cameIn} in · ${h.doneMe + h.doneOthers} done` : '',
  pastDue: h.pastDue ? `past due ${h.pastDue}` : ''
});

export interface ShiftViewModel {
  mode: 'stats' | 'message';
  message: string;
  sub: string;
  stats: ShiftStat[];
  segments: ShiftSegment[];
  aria: string;
  hours: ShiftHourView[];
  // '' when there is no older backlog.
  backlog: string;
  // false when no hour has a came-in or done bar; chartNote then replaces the bars.
  showBars: boolean;
  chartNote: string;
}

export const SHIFT_QUIET_NOTE = 'No tasks came in or were done during this shift';

const SHIFT_MESSAGES: Record<string, string> = {
  not_today: 'Not on shift today (weekly off).',
  leave: 'On leave today.',
  no_shift: 'No shift hours set. Ask your manager to set them in Sales users.'
};

const istHourOf = (t: number): number => new Date(t + IST_OFFSET_MS).getUTCHours();

// hour_start_ist may be "HH:MM" or a full timestamp.
const hourParts = (raw: string): { label: string; hour: number } | null => {
  const clock = /^(\d{1,2}):(\d{2})/.exec(raw || '');
  if (clock) return { label: `${clock[1].padStart(2, '0')}:${clock[2]}`, hour: Number(clock[1]) };
  const t = toMs(raw);
  if (Number.isNaN(t)) return null;
  const local = new Date(t + IST_OFFSET_MS);
  const hh = local.getUTCHours();
  const mm = local.getUTCMinutes();
  return { label: `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`, hour: hh };
};

// Shift start/end: "HH:MM" or a full timestamp -> "10:00 PM" (IST); '' when unreadable.
export const shiftClockLabel = (raw: string | null | undefined): string => {
  const p = hourParts(raw || '');
  if (!p) return '';
  const [hh, mm] = p.label.split(':').map(Number);
  if (!(hh >= 0 && hh < 24 && mm >= 0 && mm < 60)) return '';
  return `${hh % 12 || 12}:${String(mm).padStart(2, '0')} ${hh < 12 ? 'AM' : 'PM'}`;
};

export const SHIFT_UNAVAILABLE = 'Shift data is not available.';

export const shiftView = (
  shift: LeadsShift | null | undefined,
  level: RepLevel,
  now: number
): ShiftViewModel => {
  if (!shift) {
    return {
      mode: 'message',
      message: SHIFT_UNAVAILABLE,
      sub: '',
      stats: [],
      segments: [],
      aria: '',
      hours: [],
      backlog: '',
      showBars: false,
      chartNote: ''
    };
  }
  const startLabel = shiftClockLabel(shift.start_ist);
  const endLabel = shiftClockLabel(shift.end_ist);
  const window = startLabel && endLabel ? `${startLabel} to ${endLabel} IST` : '';
  const empty: ShiftViewModel = {
    mode: 'message',
    message: '',
    sub: window,
    stats: [],
    segments: [],
    aria: '',
    hours: [],
    backlog: '',
    showBars: false,
    chartNote: ''
  };
  if (SHIFT_MESSAGES[shift.state]) return { ...empty, message: SHIFT_MESSAGES[shift.state] };

  const doneAll = shift.done_by_me + shift.done_by_others;
  const skipped = shift.skipped_by_me + shift.skipped_by_others;
  // Compact meta only; what counts is explained in the help bullets.
  const sub = joinHint(window, 'Today', shift.state === 'off' ? 'off shift now' : '');
  const stats: ShiftStat[] = [
    {
      key: 'total',
      label: 'Total so far',
      value: String(shift.total),
      detail: `${shift.came_in} came in · ${shift.carried_in} carried in`,
      color: 'var(--text-primary)'
    },
    {
      key: 'done',
      label: 'Done',
      value: String(doneAll),
      detail: `me ${shift.done_by_me} · others ${shift.done_by_others}`,
      color: 'var(--state-success)'
    },
    {
      key: 'skipped',
      label: 'Skipped / auto-closed',
      value: `${skipped} / ${shift.auto_closed}`,
      detail: `me ${shift.skipped_by_me} · others ${shift.skipped_by_others}`,
      color: 'var(--text-muted)'
    },
    {
      key: 'left',
      label: 'Left to do',
      value: String(shift.left_not_due + shift.past_due),
      detail: `${shift.left_not_due} not due yet`,
      color: 'var(--state-info)'
    },
    {
      key: 'past',
      label: 'Past due',
      value: String(shift.past_due),
      detail: '',
      color: 'var(--state-error)'
    }
  ];

  const parts: Array<Omit<ShiftSegment, 'pct'>> = [
    { key: 'done_me', label: 'Done by me', value: shift.done_by_me, color: 'var(--state-success)' },
    {
      key: 'done_others',
      label: 'Done by others',
      value: shift.done_by_others,
      color: 'var(--state-success)',
      opacity: 0.45
    },
    { key: 'skipped', label: 'Skipped', value: skipped, color: 'var(--text-muted)' },
    {
      key: 'auto',
      label: 'Auto-closed',
      value: shift.auto_closed,
      color: 'var(--border-emphasis)'
    },
    {
      key: 'open',
      label: 'Left to do, not due yet',
      value: shift.left_not_due,
      color: 'var(--state-info)'
    },
    {
      key: 'past',
      label: 'Left to do, past due',
      value: shift.past_due,
      color: 'var(--state-error)'
    }
  ];
  const sum = parts.reduce((a, p) => a + p.value, 0);
  const segments = parts.map((p) => ({ ...p, pct: sum ? (p.value / sum) * 100 : 0 }));
  const aria = `${shift.total} tasks so far: ${shift.done_by_me} done by me, ${shift.done_by_others} done by others, ${skipped} skipped, ${shift.auto_closed} auto-closed, ${shift.left_not_due} left to do, ${shift.past_due} past due`;

  const rows = (shift.hours || [])
    .map((h) => ({ h, p: hourParts(h.hour_start_ist) }))
    .filter(
      (x): x is { h: (typeof shift.hours)[number]; p: { label: string; hour: number } } => !!x.p
    );
  const max = Math.max(
    1,
    ...rows.map(({ h }) => Math.max(h.came_in, h.done_by_me + h.done_by_others))
  );
  const nowHour = istHourOf(now);
  const hours = rows.map(({ h, p }) => {
    const current = shift.state === 'on' && p.hour === nowHour;
    const done = h.done_by_me + h.done_by_others;
    return {
      label: p.label,
      cameIn: h.came_in,
      doneMe: h.done_by_me,
      doneOthers: h.done_by_others,
      pastDue: h.past_due_at_end,
      inPct: (h.came_in / max) * 100,
      donePct: (done / max) * 100,
      doneMePct: (h.done_by_me / max) * 100,
      current,
      future: h.past_due_at_end === null && !current
    };
  });

  const showBars = hours.some((h) => h.cameIn > 0 || h.doneMe + h.doneOthers > 0);
  const backlog = shift.older_backlog
    ? `Older backlog: ${shift.older_backlog} open tasks due before yesterday`
    : '';
  return {
    mode: 'stats',
    message: '',
    sub,
    stats,
    segments,
    aria,
    hours,
    backlog,
    showBars,
    chartNote: hours.length && !showBars ? SHIFT_QUIET_NOTE : ''
  };
};

// ---- help ----

const MEDIAN_BASIS: Record<RepLevel, string> = {
  L1: 'with a lead joined or a task done in the period',
  L2: 'with at least one covered trial in the period that has taken place',
  L3: 'with at least one lead joined in the period'
};

export const helpBullets = (level: RepLevel, startIst?: string, endIst?: string): string[] => {
  const range = periodRange(startIst, endIst);
  const between = range ? `between ${range}` : 'in the period';
  const byLevel: Record<RepLevel, string[]> = {
    L1: [
      `Trials I booked: trials booked ${between}, by you or by the student within 7 days after your last done task for them ("After my task"). Each trial has one owner; statuses are as of now.`,
      'Booked and attended counts only those trials whose time has passed.'
    ],
    L2: [
      `My trials joined: trials that started ${between} where you completed a reminder or join task. Joined means the student attended.`,
      'Trials joined is out of the covered trials that have taken place; Covered trials also counts ones still upcoming today.'
    ],
    L3: [
      'Conversion: of the leads you joined in the period, how many have paid so far. Still-open leads are counted, so the rate rises as their windows run.',
      'Days trial to pay is your median over those conversions.'
    ]
  };
  return [
    ...byLevel[level],
    `Leads I joined: leads you joined ${between}; their status as of now (open with days left, paid, or window ended).`,
    'Joined & converted: those leads whose first membership purchase came while you were their advisor, at any time up to now. Fully refunded purchases and family seats do not count.',
    'Last touch: your latest call, SMS or WhatsApp, done task or note. Chat is not counted.',
    'My shift today is today only, not the period: your level’s tasks that came in during your shift, plus open ones carried in that were due since yesterday (instant tasks only while unexpired). Older open tasks show as Older backlog. Done and skipped are split into by you and by others.',
    'By hour: tasks that came in and were done in each hour of your shift; the red count is how many were past due when that hour ended.',
    `Medians compare active ${level} reps ${MEDIAN_BASIS[level]}, on the same counts.`,
    'The page refreshes itself every 5 minutes. Work your tasks from the Tasks tab.'
  ];
};

// ---- Team's Leads view ----

export interface RepOption {
  value: string;
  label: string;
  // null = no rep level (manager / super user); grouped under "Other".
  level: RepLevel | null;
}

export interface RepGroup {
  label: string;
  options: RepOption[];
}

// Never blank: name, else the email's local part, else "Unnamed (<uuid prefix>)" (matches goserver).
export const repDisplayName = (m: Pick<TeamMember, 'name' | 'email' | 'ops_uuid'>): string => {
  const name = (m.name || '').trim();
  if (name) return name;
  const local = (m.email || '').trim().split('@')[0];
  return local || `Unnamed (${(m.ops_uuid || '').slice(0, 8)})`;
};

const byLabel = (a: RepOption, b: RepOption) =>
  a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });

// Active L1-L3 reps only: the server answers target_not_rep for anyone else.
export const teamRosterOptions = (members: TeamMember[]): RepOption[] =>
  members
    .filter((m) => m.status === 'ACTIVE' && !!m.level && (LEVELS as string[]).includes(m.level))
    .map((m) => ({ value: m.ops_uuid, label: repDisplayName(m), level: m.level as RepLevel }))
    .sort(byLabel);

// L1, L2, L3 groups (names A-Z, level in the group label), then "Other"; empty groups dropped.
export const groupRepOptions = (options: RepOption[]): RepGroup[] => {
  const groups: RepGroup[] = [
    ...LEVELS.map((lvl) => ({ label: lvl, options: options.filter((o) => o.level === lvl) })),
    {
      label: 'Other',
      options: options.filter((o) => !o.level || !(LEVELS as string[]).includes(o.level))
    }
  ];
  return groups
    .map((g) => ({ ...g, options: [...g.options].sort(byLabel) }))
    .filter((g) => g.options.length > 0);
};

export const REP_PARAM = 'rep';

export const NOT_ON_ROSTER = "You're not on the sales roster yet — ask your manager.";

export const repFromSearch = (search: string): string =>
  new URLSearchParams(search || '').get(REP_PARAM) || '';

export const searchWithRep = (search: string, opsUuid: string): string => {
  const params = new URLSearchParams(search || '');
  if (opsUuid) params.set(REP_PARAM, opsUuid);
  else params.delete(REP_PARAM);
  const out = params.toString();
  return out ? `?${out}` : '';
};

const REASONS: Record<string, string> = {
  not_manager: 'Only sales managers and super users can view another rep.',
  target_not_rep: 'This person is not an active L1, L2 or L3 rep.',
  bad_period: 'That period is not supported.',
  my_leads_timeout: 'Taking longer than usual — retrying'
};

// Server build timed out; transient, so polling carries on.
export const isLeadsTimeout = (status: number | undefined, reason: string): boolean =>
  status === 503 && reason === 'my_leads_timeout';

export const leadsErrorMessage = (status: number | undefined, reason: string): string => {
  if (reason && REASONS[reason]) return REASONS[reason];
  if (status === 404) return 'That team member was not found.';
  if (status === 403) return REASONS.not_manager;
  return reason ? `Could not load leads: ${reason}` : 'Could not load leads.';
};
